import { PanelLeftClose, PanelLeftOpen, Plus } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';

import { BrandLogo } from '../../../components/common/BrandLogo';
import { cn } from '../../../lib/cn';
import { Button, Hint, IconButton } from '../../../ui';
import { PRIMARY_NAV, SETTINGS_NAV, isNavActive, type NavItem } from './nav';

export interface SidebarNavProps {
  /** Icon-only rail (user preference, or the builder taking the width). */
  collapsed: boolean;
  onToggleCollapsed?: () => void;
  counts: { charts: number; dashboards: number; sources: number };
  /** Called after a navigation — closes the mobile drawer. */
  onNavigate?: () => void;
  /** Inside the mobile drawer the collapse control and brand row are redundant. */
  variant?: 'fixed' | 'drawer';
  onNewChart?: () => void;
}

export function SidebarNav({
  collapsed,
  onToggleCollapsed,
  counts,
  onNavigate,
  variant = 'fixed',
  onNewChart,
}: SidebarNavProps) {
  const { pathname } = useLocation();
  const rail = collapsed && variant === 'fixed';

  const countFor = (item: NavItem) => (item.count ? counts[item.count] : undefined);

  const row = (item: NavItem) => {
    const active = isNavActive(item, pathname);
    const Icon = item.icon;
    const count = countFor(item);
    const content = (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === '/'}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'group flex items-center rounded-md text-[0.8125rem] font-medium transition-colors',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
          rail ? 'size-9 justify-center' : 'gap-2.5 px-2.5 py-2',
          active
            ? 'bg-accent-subtle text-accent-text'
            : 'text-fg-secondary hover:bg-surface-hover hover:text-fg',
        )}
      >
        <Icon className={cn('size-4 shrink-0', active ? 'text-accent-text' : 'text-fg-faint group-hover:text-fg-muted')} />
        {!rail && (
          <>
            <span className="flex-1 truncate">{item.label}</span>
            {count != null && count > 0 && (
              <span className="text-[0.6875rem] text-fg-faint tabular">{count}</span>
            )}
          </>
        )}
      </NavLink>
    );
    return rail ? (
      <Hint key={item.to} content={item.label} side="right">
        {content}
      </Hint>
    ) : (
      content
    );
  };

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col border-r border-border bg-surface',
        variant === 'fixed' && (rail ? 'w-14' : 'w-60'),
      )}
    >
      <div className={cn('flex items-center gap-2 px-3 pb-1 pt-3', rail && 'justify-center px-0')}>
        {rail ? (
          <BrandLogo variant="symbol" className="size-6" />
        ) : (
          <>
            <BrandLogo className="h-5 w-auto" />
            {variant === 'fixed' && onToggleCollapsed && (
              <IconButton
                label="Collapse sidebar"
                size="sm"
                className="ml-auto"
                onClick={onToggleCollapsed}
              >
                <PanelLeftClose />
              </IconButton>
            )}
          </>
        )}
      </div>

      {rail && onToggleCollapsed && (
        <div className="flex justify-center pb-1 pt-1">
          <Hint content="Expand sidebar" side="right">
            <IconButton label="Expand sidebar" size="sm" onClick={onToggleCollapsed}>
              <PanelLeftOpen />
            </IconButton>
          </Hint>
        </div>
      )}

      <div className={cn('px-3 pb-2 pt-2', rail && 'flex justify-center px-0')}>
        {rail ? (
          <Hint content="New chart" side="right" shortcut="C">
            <IconButton label="New chart" variant="primary" onClick={onNewChart}>
              <Plus />
            </IconButton>
          </Hint>
        ) : (
          <Button variant="primary" size="md" block icon={<Plus />} onClick={onNewChart}>
            New chart
          </Button>
        )}
      </div>

      <nav
        aria-label="Main"
        className={cn('scroll-region flex min-h-0 flex-1 flex-col gap-0.5', rail ? 'items-center px-2' : 'px-2')}
      >
        {PRIMARY_NAV.map(row)}

        <div className="mt-auto flex flex-col gap-0.5 pb-2 pt-4">{row(SETTINGS_NAV)}</div>
      </nav>
    </div>
  );
}
